// @flow
import './Form.css';

import * as React from 'react';
import _ from 'lodash';
import Input from './Input';
import Label from './Label';
import Separator from './Separator';
import Switch from './Switch';
import TextBox from './TextBox';
import Dropdown from '../Dropdown';
import RadioButton from './RadioButton';
import NumberInput from './NumberInput';
import Checkbox from './Checkbox';
import FileInput from './FileInput';
import AutocompleteTextBox from './AutocompleteTextBox';
import HighlightText from '../HighlightText';
import ExpandButton from '../ExpandButton';
import Button from '../Button';
import MetadataInputComponent from '../../components/metadata/MetadataInputComponent';
import DatePicker from '../../common/form/DatePicker';
import classNames from 'classnames';
import Action from '../Action';
import TextWithEllipsis from '../TextWithEllipsis';
import { toast } from '../../store/StoreUtil';
import ShowMoreText from '../../tabs/collections/ShowMoreText';
import { ThemeSetup } from '../../themes/ThemeSetup';
import AutoCompleteComponent from './advanceSearchComponent/AutoComplateComponent';
import MetadataUtil from '../../util/MetadataUtil';
import { ClipBoardUtil } from '../../util/ClipBoardUtil';
import ToolTip from '../AssetPropertyView';
import StringUtil from '../../util/StringUtil';

const Components = {
  Input,
  Label,
  Separator,
  Switch,
  TextBox,
  Dropdown,
  RadioButton,
  NumberInput,
  Checkbox,
  FileInput,
  AutocompleteTextBox,
  MetadataInputComponent,
  DatePicker
};

type Props = {
  fields: Object[],
  className?: string,
  pattern?: string,
  searchType?: 'KEYS' | 'VALUES' | 'ALL',
  showTimeline?: boolean,
  disabled?: boolean,
  onChange: Function,
  onLocateClick?: Function,
  onDelete?: Function
};

type State = {
  collapsedSubfields: Object
};

function hasStartStopFields(field: Object) {
  if (!(field && field.__data)) {
    return false;
  }

  const keys = Object.keys(field.__data);
  return _.includes(keys, 'startTimeMs') && _.includes(keys, 'stopTimeMs');
}

function normalizeEventArgs(...args: any[]) {
  const event: Event = args[0];
  if (typeof event !== 'object') {
    args[0] = { target: { value: event } };
  }

  return args;
}

export default class Form extends React.Component<Props, State> {
  static Types = {
    SUBFIELD: 'Subfield',
    TEXT: 'TextBox',
    AUTOCOMPLETE: 'AutocompleteTextBox',
    DROPDOWN: 'Dropdown',
    METADATA: 'MetadataInputComponent',
    DATE: 'DatePicker'
  };

  static defaultProps = {
    className: ''
  };

  static _currentTabIndex: number = 0;

  state = {
    collapsedSubfields: {}
  };

  onToggle = (key: string) => {
    const collapsedSubfields = { ...this.state.collapsedSubfields };
    if (collapsedSubfields[key]) {
      delete collapsedSubfields[key];
    } else {
      collapsedSubfields[key] = true;
    }
    this.setState({ collapsedSubfields });
  };

  wrapOnChange = (field: Object) => {
    return (...args: any[]) => {
      const normalizedArgs = normalizeEventArgs(...args);
      return this.props.onChange(...normalizedArgs, field);
    };
  };

  onDoubleClick = (text) => {
    ClipBoardUtil.copyToClipBoard(text);
  };
  autoCompleteOnchange = (data: string) => {
    this.props.onChange('', '', data);
  };

  renderField = (field: Object, i: number) => {
    if (!field) {
      return null;
    }

    const { collapsedSubfields } = this.state;
    const {
      pattern,
      searchType,
      showTimeline,
      disabled,
      onChange,
      onLocateClick,
      onDelete,
      autoCompleteProp,
      fromComponent
    } = this.props;
    const newAutocompleteProp = autoCompleteProp
      ? {
          ...autoCompleteProp,
          onChange: this.autoCompleteOnchange,
          className: 'clearable size-small',
          tagName: 'TextBox',
          isClearButton: true
        }
      : null;
    const { type, label, value, invalid, hideLabel, ...rest } = field;
    const valuetext = pattern && searchType !== 'LABELS' ? <HighlightText text={value} pattern={pattern} /> : value;
    const text = pattern && searchType !== 'VALUES' ? <HighlightText text={label} pattern={pattern} /> : label;
    const isExpanded = !collapsedSubfields[field.label];
    if (type === Form.Types.SUBFIELD) {
      const showLocateBtn = hasStartStopFields(field);
      return (
        <div key={i} className="subfield">
          <header>
            <ExpandButton isExpanded={isExpanded} onClick={() => this.onToggle(field.label)} />
            <label className="formfield-label">{label || 'Object'}</label>
            {showTimeline && showLocateBtn && onLocateClick ? (
              <Button
                type="PLAIN"
                className="locate"
                text={t('LOCALTE_ON_TIMELINE')}
                onClick={() => onLocateClick(field)}
              />
            ) : null}
          </header>
          {isExpanded ? <div className="subfield-body">{field.fields.map(this.renderField)}</div> : null}
        </div>
      );
    }

    const Component = Components[type];
    if (!Component) {
      throw new Error(`No input component found for type ${type}`);
    }

    const tabIndex = ++Form._currentTabIndex;

    let props;
    if (onChange) {
      props = {
        valuetext,
        tabIndex,
        disabled,
        onChange: this.wrapOnChange(field),
        value: valuetext,
        fromComponent
      };
    } else {
      props = { disabled, defaultValue: value, tabIndex, fromComponent };
    }
    return (
      <div key={i} className="formfield" style={{ alignItems: 'baseline' }}>
        <Action custom predicates={[() => !hideLabel]}>
          <label className="formfield-label">{text}</label>
        </Action>
        {disabled ? (
          <div className={classNames('formfield-input', { invalid: invalid }, 'form-text-disabled')}>
            <div style={{ userSelect: 'all' }} className="size-small" onDoubleClick={() => this.onDoubleClick(value)}>
              {this.props.fromComponent === 'TimelinePopup' ? (
                <ToolTip
                  text={valuetext}
                  style={{
                    overflow: 'hidden',
                    maxWidth: '162px',
                    display: 'inline-flex'
                  }}>
                  {StringUtil.isURL(valuetext) ? (
                    <a
                      href={valuetext}
                      target="_blank"
                      style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                      {valuetext}
                    </a>
                  ) : (
                    <p style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', margin: 'unset' }}>
                      {valuetext}
                    </p>
                  )}
                </ToolTip>
              ) : StringUtil.isURL(valuetext) ? (
                <a href={valuetext} target="_blank">
                  {valuetext}
                </a>
              ) : (
                <ShowMoreText lines={2}>
                  <p>{valuetext}</p>
                </ShowMoreText>
              )}
            </div>
          </div>
        ) : (
          <div className={classNames('formfield-input', { invalid: invalid })}>
            {text === 'key' ? (
              <TextWithEllipsis
                text={MetadataUtil.getPropInfo(valuetext) ? MetadataUtil.getPropInfo(valuetext).getKey() : valuetext}
              />
            ) : newAutocompleteProp && text === 'Value' ? (
              <AutoCompleteComponent {...newAutocompleteProp} />
            ) : (
              <Component {...props} {...rest} />
            )}
            {/* // <Component {...props} {...rest} /> */}
          </div>
        )}
        {/* <div className={classNames('formfield-input', { invalid: invalid })}>
          <Component {...props} {...rest} />
        </div> */}
        {disabled ? null : (
          <Action custom predicates={[() => i === 0 && _.isFunction(onDelete)]}>
            <img src={ThemeSetup.getImageURL('trash_red.svg')} onClick={onDelete} className="delete-button" />
          </Action>
        )}
      </div>
    );
  };

  render() {
    const { fields, className } = this.props;
    return (
      <div name="Form" className={className}>
        {fields.map(this.renderField)}
      </div>
    );
  }
}
